import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiGetVenues, apiDeleteVenue, type VenueDTO } from '../api';
import { useWriteGate } from '../lib/writeGate';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';
import PageLoadError from '../components/PageLoadError';

// Deterministic color from the venue name (keeps the color-dot consistent per venue)
function venueColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return `hsl(${h}, 55%, 55%)`;
}

export default function VenuesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Capability-aware write gate (lot A2). `/api/venues` is mounted AFTER the
  // server's global read-only guard (server/index.js:519-525; catalog-write
  // guard at :579-582, router at :596) → DELETE 403s a read-only account
  // (and a viewer: requireCatalogWrite wants editor+ on a band).
  const writeGate = useWriteGate();
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWrite;
  const [venues, setVenues] = useState<VenueDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Raw API message for the shared state's context line (never an invented cause).
  const [loadErrorDetail, setLoadErrorDetail] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [desc, setDesc] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    setLoadErrorDetail(null);
    apiGetVenues()
      .then(setVenues)
      .catch((e: unknown) => {
        console.error(e);
        setLoadError(true);
        setLoadErrorDetail(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = venues.filter((v) => !q || (v.name || '').toLowerCase().includes(q));
    return [...list].sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      return desc ? bn.localeCompare(an) : an.localeCompare(bn);
    });
  }, [venues, query, desc]);

  const remove = async (id: string) => {
    if (!writeGate.canWrite) return;
    if (!(await showConfirm(t('venues.deleteConfirm')))) return;
    await apiDeleteVenue(id);
    load();
  };

  // Lot 3a: an empty venue list is a legitimate state ("no venues yet"), so a
  // failed read must not be dressed up as one — it gets its own readable state.
  if (loadError) {
    return <PageLoadError title={t('venues.title')} detail={loadErrorDetail} onRetry={load} />;
  }

  return (
    <div className="page">
      <PageHeader
        title={withPageIcon('venues.title', t('venues.title'))}
        actions={
          <button className="btn btn-primary" onClick={() => navigate('/tour/venues/new')} disabled={!writeGate.canWrite}>
            {t('venues.newVenue')}
          </button>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 180 }}
          placeholder={t('venues.filterPh')}
          value={query}
          data-venue-filter
          onChange={(e) => setQuery(e.target.value)}
        />
        <button
          className="btn btn-secondary"
          onClick={() => setDesc((d) => !d)}
          title={t('venues.toggleSort')}
          data-venue-sort
        >
          {desc ? t('venues.sortDesc') : t('venues.sortAsc')}
        </button>
      </div>

      {loading ? (
        <div className="loading">{t('venues.loading')}</div>
      ) : visible.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ color: 'var(--text-dim)' }}>
            {venues.length === 0 ? t('venues.noVenues') : t('venues.noMatch')}
          </p>
        </div>
      ) : (
        // P3a: the dense <table> is now one card stack (`.show-card` language),
        // ONE markup at every width — a row is never duplicated for mobile.
        <div className="rc-list" data-venues-list>
          {visible.map((v) => (
            <div
              key={v.id}
              className="rc-card clickable"
              data-venue-card={v.id}
              onClick={() => navigate(`/tour/venues/${v.id}`)}
            >
              <span
                className="rc-card-dot"
                style={{ background: venueColor(v.name || '') }}
                aria-hidden="true"
              />
              <div className="rc-card-body">
                <div className="rc-card-name">{v.name || t('venues.unnamed')}</div>
                <div className="rc-card-meta">
                  {v.address && <span>{v.address}</span>}
                  {v.city && <span>{v.city}</span>}
                  {v.country && <span>{v.country}</span>}
                </div>
              </div>
              {v.capacity != null && <span className="rc-chip">👥 {v.capacity}</span>}
              <div className="rc-card-actions">
                <button
                  className="btn btn-small btn-danger"
                  disabled={!writeGate.canWrite}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(v.id);
                  }}
                >
                  {t('venues.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
